import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { bulkAudienceSelectBoardThunk, duplicateAudienceBoardForCampaignsThunk } from '@common-reducers/BoardsThunks';
import { setActiveBulkView } from '@common-reducers/CampaignReducer';
import { getIsAudienceRowChecked, getTaskSumByBoardIdRecord } from '@common-reducers/CampaignSelectors';
import { handleActiveBulkViewNavigationThunk, toggleAudienceCheckboxThunk } from '@common-reducers/CampaignThunks';
import { DBBoardsThunks, boardsSelectOneFieldByQuery } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useRouter } from '@common-reducers/hooks/use-router';
import { isExtensionContextOrWhatsappView } from '@common-services/detect-context';
import { audienceBoardPath } from '@common/utils/campaign-routes';
import { useCampaignsRedesignNav } from '@extension/context/content/components/campaigns-redesign/nav-context';
import { namespace } from '@common/config/constants';
import { AlertCircle, Loader2, MoreVertical } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsPencil, BsTrash } from 'react-icons/bs';
import { MdOutlineContentCopy } from 'react-icons/md';


type Props = {
    index: any;
    itemId: string;
}

export default function BulkAudienceSelectRow({
    index,
    itemId,
}: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const router = useRouter();
    // Under the campaigns redesign shell this is non-null and we navigate to the
    // board's own URL instead of flipping Redux in place. Null for legacy /
    // extension callers → fall through to the existing behaviour unchanged.
    const redesignNav = useCampaignsRedesignNav();
    const boardId = useAppSelector((state) => boardsSelectOneFieldByQuery(state.DBBoardsReducer, {
        _id: itemId,
    }, '_id')) as string | undefined
    const boardName = useAppSelector((state) => boardsSelectOneFieldByQuery(state.DBBoardsReducer, {
        _id: itemId,
    }, 'name')) as string | undefined
    // Uses the same fallback (board.tasksCount → local task count) as the
    // campaign-wide `getIsCampaignWithoutContacts` gate, so a freshly-created
    // board with no server-side tasksCount denormalization still shows the
    // right number here AND unblocks the Next button.
    const taskCount = useAppSelector((state) => getTaskSumByBoardIdRecord(state)[itemId]) as number | undefined

    const activeBulkView = useAppSelector((state) => state.CampaignReducer.activeBulkView);
    const checked = useAppSelector((state) => getIsAudienceRowChecked(state, itemId))
    const [isLoadingDuplicateBoard, setIsLoadingDuplicateBoard] = useState(false)

    const [open, setOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const numberOfContacts = taskCount;

    const triggerToggleAudienceCheckbox = (_event: unknown) => {
        dispatch(toggleAudienceCheckboxThunk({
            boardId: boardId ?? ''
        }))
    }


    const handleOpenMenu = (event: React.MouseEvent) => {
        event.stopPropagation()
        setOpen(true);
    };

    const handleCloseMenu = (event?: React.MouseEvent) => {
        event?.stopPropagation();
        setOpen(false);
    };

    const handleDuplicateClick = async (e: React.MouseEvent) => {
        e.stopPropagation()
        setOpen(false)
        setIsLoadingDuplicateBoard(true)
        const newBoardId = await dispatch(duplicateAudienceBoardForCampaignsThunk({ oldBoardId: itemId }))

        await dispatch(DBBoardsThunks.find({
            deleted: false,
            type: 'audience',
            $sort: { createdAt: -1 }
        }))

        setIsLoadingDuplicateBoard(false)
    }

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(false);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        dispatch(DBBoardsThunks.patch({
            entity: {
                _id: boardId,
                deleted: true,
            },
        }))
        setIsDeleteDialogOpen(false);
    };

    const handelEditClick = (e: React.MouseEvent) => {
        e.stopPropagation()

        // Redesign: URL-drive the board editor; skip the legacy Redux flip + router.navigate.
        if (redesignNav) {
            redesignNav.openBoard(itemId)
            return
        }

        dispatch(setActiveBulkView(activeBulkView === "campaignManagementView" ? "campaignBoardFromManagementView" : "campaignBoardFromNewCampaignView"))
        dispatch(bulkAudienceSelectBoardThunk({ boardId: itemId }))

        // Website only: deep-link the audience board (matches the /tasks/?boardID= convention).
        // Only from the management view — the new-campaign wizard keeps its Redux-only flow.
        if (activeBulkView === "campaignManagementView" && !isExtensionContextOrWhatsappView()) {
            router.navigate(audienceBoardPath(itemId))
        }
    }

    const handleAudienceRowClick = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation()
        e.stopPropagation()
        // Redesign (management context only): URL-drive the board editor. In the
        // wizard's SELECTION mode the row toggles a checkbox, not navigation, so
        // only redirect the management-view open; selection falls through.
        if (redesignNav && activeBulkView === "campaignManagementView") {
            redesignNav.openBoard(itemId)
            return
        }
        if (activeBulkView === "campaignManagementView") {
            dispatch(handleActiveBulkViewNavigationThunk({ navigate: "toCampaignBoardView", boardId: itemId }))
        } else {
            triggerToggleAudienceCheckbox(boardId)
        }
    }



    return (
        <div 
            key={index}
            className={cn(
                "flex flex-row justify-between items-center w-full box-border",
                "px-6 py-4",
                "divide-y-0",
                checked && activeBulkView !== "campaignManagementView" 
                    ? "bg-sky-50 dark:bg-sky-950/30" 
                    : "hover:bg-slate-50 dark:hover:bg-slate-800/50",
                activeBulkView !== "campaignManagementView" && "cursor-pointer",
                activeBulkView === "campaignManagementView" && "cursor-pointer",
                "transition-colors duration-150"
            )}
            onClick={(e) => handleAudienceRowClick(e)}
        >
            <div className="flex items-center gap-4 flex-1 min-w-0">
                {activeBulkView !== "campaignManagementView" && (
                    <div className="flex-shrink-0 pointer-events-none">
                        <Checkbox
                            id={`checkbox_${boardName}`}
                            checked={checked ?? false}
                            className={cn(
                                "border-slate-700 dark:border-slate-400"
                            )}
                            /* Was the legacy brand blue #259ae9, hard-coded and identical in
                               both themes — while the redesign's audience list two clicks
                               away paints its brand #4F46E5 / #818CF8. One concept, two
                               colours. `--bt-blue` / `--on-brand` are the campaigns-redesign
                               tokens and DO flip (white on #4F46E5 = 6.29:1; #14151E on
                               #818CF8 = 6.04:1). */
                            style={(checked ?? false) ? {
                                borderColor: "var(--bt-blue, #2B7FFF)",
                                backgroundColor: "var(--bt-blue, #2B7FFF)",
                                color: "var(--on-brand, #FFFFFF)"
                            } : undefined}
                            name={boardName}
                        />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h2 className={cn(
                        "font-semibold text-slate-900 dark:text-slate-50 truncate"
                    )}>
                        {boardName}
                    </h2>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                    {numberOfContacts !== -1 && (
                        <span className={cn(
                            "text-sm font-medium px-3 py-1 rounded-full flex-shrink-0",
                            checked && activeBulkView !== "campaignManagementView"
                                ? "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300"
                                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                        )}>
                            {t('campaign.contacts', { count: numberOfContacts })}
                        </span>
                    )}
                    {numberOfContacts === -1 && (
                        <Skeleton className="w-20 h-6 flex-shrink-0 rounded-full" />
                    )}
                    {!isLoadingDuplicateBoard && (
                        <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                            <DropdownMenu open={open} onOpenChange={setOpen}>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleOpenMenu}
                                        className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 h-8 w-8 p-0"
                                    >
                                        <MoreVertical className="h-5 w-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    className={cn(
                                        "w-[245px] p-0",
                                        "bg-white dark:bg-slate-800",
                                        "border border-slate-200 dark:border-slate-700",
                                        "rounded-xl shadow-md"
                                    )}
                                    align="end"
                                >
                                    <DropdownMenuItem
                                        onClick={(e) => handelEditClick(e)}
                                        className="cursor-pointer"
                                    >
                                        <BsPencil className="h-4 w-4 me-2" />
                                        {t('audiences.editAudience')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={(e) => handleDuplicateClick(e)}
                                        className="cursor-pointer"
                                    >
                                        <MdOutlineContentCopy className="h-4 w-4 me-2" />
                                        {t('audiences.duplicate')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={handleDeleteClick}
                                        className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/20"
                                    >
                                        <BsTrash className="h-4 w-4 me-2" />
                                        {t('audiences.deleteAudience')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>
            </div>
            {isLoadingDuplicateBoard && (
                <div className="overflow-hidden flex justify-end items-center flex-shrink-0 ms-2">
                    <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--bt-blue, #2B7FFF)" }} />
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="max-w-md p-6 sm:p-8">
                    <DialogHeader className="space-y-3">
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            {t('audiences.deleteAudience')}?
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground">
                            {t('audiences.deleteAudienceConfirmation', {
                                name: boardName,
                                defaultValue: `Are you sure you want to delete "${boardName}"? This action cannot be undone.`
                            })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('common.cancel', { defaultValue: 'Cancel' })}
                        </Button>
                        <Button variant="destructive" onClick={handleConfirmDelete}>
                            {t('common.delete', { defaultValue: 'Delete' })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}